import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckedState } from '@radix-ui/react-checkbox';

import { Button, Paragraph, Checkbox } from '@app/shared/ui/atoms/Common';
import { ListItemRow } from '@app/shared/ui/atoms/Admin/Withdrawals';
import { ConfirmationModal } from '@app/shared/ui/organisms/Common';
import { formatBalance } from '@app/shared/utils/common';
import { PriceType } from '@app/shared/types/common';
import {
  WithdrawalStatus,
  WithdrawalReviewStatus,
} from '@app/store/slices/withdrawal/withdrawalTypes';

interface BottomSectionProps {
  status: WithdrawalStatus;
  onStatusChange: (value: WithdrawalReviewStatus) => void;
  userBalance?: string;
  userFrozenBalance?: string;
}

export function BottomSection({
  status,
  onStatusChange,
  userBalance,
  userFrozenBalance,
}: BottomSectionProps) {
  const [isBalanceChecked, setIsBalanceChecked] = useState<CheckedState>(false);
  const [isTonTransferred, setIsTonTransferred] = useState<CheckedState>(false);
  const [modalStatus, setModalStatus] = useState<WithdrawalReviewStatus | null>(null);
  const { t } = useTranslation();

  const handleModalClose = () => {
    setModalStatus(null);
  };

  const handleStatusChange = () => {
    if (modalStatus) {
      onStatusChange(modalStatus);
      handleModalClose();
    }
  };

  const changeStatusLabel = t('admin_withdrawal_details.change_status');
  const reviewingLabel = t('withdrawal.statuses.reviewing');
  const confirmedLabel = t('withdrawal.statuses.confirmed');
  const rejectedLabel = t('withdrawal.statuses.rejected');

  return (
    <section className="flex flex-col gap-2">
      {status === WithdrawalStatus.PENDING && (
        <Button
          variant="secondary"
          onClick={() => setModalStatus(WithdrawalStatus.REVIEWING)}
        >{`${changeStatusLabel} ${reviewingLabel}`}</Button>
      )}

      {status === WithdrawalStatus.REVIEWING && (
        <>
          {!!userBalance && !!userFrozenBalance ? (
            <>
              <ListItemRow
                label={t('common.balance')}
                value={<b>{formatBalance(PriceType.TON, userBalance)}</b>}
              />
              <ListItemRow
                label={t('admin_withdrawal_details.frozen_balance')}
                value={<b>{formatBalance(PriceType.TON, userFrozenBalance)}</b>}
              />
            </>
          ) : (
            <Paragraph>⚠️ {t('admin_withdrawal_details.user_balance_problem')}</Paragraph>
          )}
          <div className="flex items-center gap-2 text-sm">
            <Checkbox checked={isBalanceChecked} onCheckedChange={setIsBalanceChecked} />
            {t('admin_withdrawal_details.balance_checked')}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Checkbox checked={isTonTransferred} onCheckedChange={setIsTonTransferred} />
            {t('admin_withdrawal_details.ton_transferred')}
          </div>
          <Button
            variant="secondary"
            className="disabled:opacity-50"
            disabled={!isBalanceChecked || !isTonTransferred}
            onClick={() => setModalStatus(WithdrawalStatus.CONFIRMED)}
          >{`${changeStatusLabel} ${confirmedLabel}`}</Button>
        </>
      )}

      <Button
        variant="secondary"
        onClick={() => setModalStatus(WithdrawalStatus.REJECTED)}
      >{`${changeStatusLabel} ${rejectedLabel}`}</Button>

      <ConfirmationModal
        isOpen={!!modalStatus}
        onOpenChange={handleModalClose}
        actionType="confirm"
        withCustomTitle={true}
        title={
          <>
            {t('confirmation_modal.title_prefix')} {changeStatusLabel.toLowerCase()}{' '}
            <span className="text-accent">{t(`withdrawal.statuses.${modalStatus}`)}</span>?
          </>
        }
        hiddenTitle="Change withdrawal status"
        onCancel={handleModalClose}
        onConfirm={handleStatusChange}
      />
    </section>
  );
}
